import { joinBackendUrl } from '../platform/backendClient'

/**
 * The injected adapter behind the generated `BackendApi` (./client.ts).
 *
 * Distinct from `platform/backendClient.ts`'s `BackendClient`, which the sync engine
 * (`lib/sync/syncActor.ts`) and the Plaid Hosted Link bridge (`lib/plaid/hostedLink.ts`)
 * keep using unchanged -- docs/plans/native-app-sdk-seams.md says to move `lib/sync/`
 * "verbatim; do not refactor it on the way", and `BackendClient` deliberately does not
 * handle Authorization, leaving each caller to resolve its own token.
 *
 * `HttpTransport` is the stricter seam Stage 2 introduces for the *typed* generated
 * client: it owns the base URL AND the Authorization header, so a caller of a typed
 * operation never has to remember to attach a bearer token by hand. See
 * docs/plans/native-app-sdk-seams.md, Stage 2 Step 2.
 */
export interface HttpTransport {
  /**
   * Performs a request against the backend. `path` is origin-relative and must start
   * with a slash; the implementation supplies the base URL and the `Authorization`
   * header. Non-2xx responses are returned, not thrown -- `./client.ts`'s `request()`
   * helper is what turns a non-2xx into a `HealthShareApiError`.
   */
  fetch(path: string, init?: RequestInit): Promise<Response>
}

/**
 * Resolves the current access token for a request. May be synchronous (a value read
 * from a closure, e.g. a React hook's already-known session token) or asynchronous (an
 * awaited `client.auth.getSession()` call) -- `createHttpTransport` awaits either way.
 * Returning `undefined` sends the request with no Authorization header, exactly like
 * the pre-Stage-2 `useBackendAuth().getAuthHeaders()` did for a signed-out caller.
 */
export type AccessTokenProvider = () => string | undefined | Promise<string | undefined>

export interface HttpTransportOptions {
  /**
   * Prepended to every path. openapi.yaml's paths are all relative to the `/backend`
   * server prefix (its `servers:` block), so the default is `/backend` -- origin-relative,
   * which is what makes requests resolve on the web app (Kong fronts the web app and the
   * admin app on one origin). A native client, which has no document origin, supplies a
   * real base URL instead (e.g. `https://app.healthsharetech.com/backend`).
   */
  baseUrl?: string
  getAccessToken: AccessTokenProvider
  /** The platform's fetch implementation. Defaults to the global `fetch`. */
  fetch?: typeof globalThis.fetch
}

export function createHttpTransport(options: HttpTransportOptions): HttpTransport {
  const baseUrl = options.baseUrl ?? '/backend'

  return {
    async fetch(path, init = {}) {
      const accessToken = await options.getAccessToken()
      const headers = new Headers(init.headers)
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`)
      }

      // Resolved on every call, not captured once at construction -- a test (or
      // real instrumentation) that reassigns `globalThis.fetch` after a memoized
      // transport already exists must still be picked up, exactly like a plain
      // `fetch(...)` call would be. See webBackendClient.ts, which relies on the
      // same always-fresh lookup.
      const platformFetch = options.fetch ?? globalThis.fetch
      return platformFetch(joinBackendUrl(baseUrl, path), { ...init, headers })
    },
  }
}

/**
 * Thrown by `./client.ts`'s generated `BackendApi` methods for any non-2xx response.
 * `code` is the backend's machine-readable error code where one is present (e.g.
 * `family_plan_required` from `inviteSpouse`); most operations don't send one, in which
 * case it's `undefined` and callers fall back to `message` or `status`.
 */
export class HealthShareApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
  ) {
    super(message)
    this.name = 'HealthShareApiError'
  }
}
